/* eslint-disable @typescript-eslint/no-var-requires */

let logger;

if (typeof window === 'undefined') {
  const { createLogger, format, transports } = require('winston');

  const _logger = createLogger({
    format: format.json(),
    transports: [new transports.Console({ handleExceptions: true })],
  });

  logger = {
    log: (message, messageContext, status) => {
      _logger.log(status ?? 'info', message, messageContext);
    },
    debug: (tag, message, messageContext) =>
      _logger.debug(`[${tag}] ${message}`, messageContext),
    info: (message, messageContext) => _logger.info(message, messageContext),
    warn: (message, messageContext) => _logger.warn(message, messageContext),
    error: (message, messageContext) => _logger.error(message, messageContext),
  };
} else {
  const {
    LogLevel,
    getInternalFaroFromGlobalObject,
  } = require('@grafana/faro-web-sdk');

  const consoleMethods = { error: 'error', warn: 'warn' };

  // Ships browser logs to Loki via the Faro collector. Falls back to the
  // console when Faro is not initialized (e.g. local dev without FARO_URL).
  //
  // Resolve the instance per call instead of destructuring `faro` at module load:
  // initializeFaro -> registerFaro *reassigns* the module's `faro` export rather
  // than mutating it, so a CJS `require` destructure captures the pre-init stub
  // permanently — `config` stays undefined and `api` stays the noop. This module
  // is required from _app.tsx before Faro initializes, so it always lost that
  // race: every log fell through to the console, where Faro's console
  // instrumentation re-collected it with the arguments stringified, which is why
  // they arrived as "<message> [object Object]" with their context lost and why
  // logger.error entries showed up as Faro exceptions rather than logs.
  // (utils/faro.ts is unaffected: an ESM `import` is a live binding, so it sees
  // the reassignment.)
  const pushLog = (level, message, messageContext) => {
    const instance = getInternalFaroFromGlobalObject();

    if (instance) {
      instance.api.pushLog([message], {
        level,
        context: messageContext,
      });
    } else {
      // eslint-disable-next-line no-console
      console[consoleMethods[level] ?? 'log'](message, messageContext ?? '');
    }
  };

  logger = {
    log: (message, messageContext, status) =>
      pushLog(status ?? LogLevel.INFO, message, messageContext),
    debug: (tag, message, messageContext) =>
      pushLog(LogLevel.DEBUG, `[${tag}] ${message}`, messageContext),
    info: (message, messageContext) =>
      pushLog(LogLevel.INFO, message, messageContext),
    warn: (message, messageContext) =>
      pushLog(LogLevel.WARN, message, messageContext),
    error: (message, messageContext) =>
      pushLog(LogLevel.ERROR, message, messageContext),
  };
}

module.exports = logger;
